import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Icon, Input, Select, Textarea, SimpleGrid,
  useToast, Badge, Divider, Table, Thead, Tbody, Tr, Th, Td, IconButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2, FiPlus } from 'react-icons/fi';
import { decoracionApi, UPLOADS_BASE, SmArchivo, DecoracionPersonal, DecoracionActividad } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  evento: any;
  onGuardado: () => void;
}

const MOMENTOS: ('ANTES' | 'DESPUES')[] = ['ANTES', 'DESPUES'];
const TIPOS_ACT = ['Instalacion', 'Mantenimiento', 'Retiro'];
const ESTATUS_ACT = ['Pendiente', 'En proceso', 'Finalizada'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={2} mt={2}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);

const ModalDecoracionDetalle: React.FC<Props> = ({ isOpen, onClose, evento, onGuardado }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const antesRef = useRef<HTMLInputElement>(null);
  const despuesRef = useRef<HTMLInputElement>(null);
  const { isOpen: isOpenDel, onOpen: onOpenDel, onClose: onCloseDel } = useDisclosure();

  const [detalle, setDetalle] = useState<any>(evento);
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [comentario, setComentario] = useState('');
  const [archivoDel, setArchivoDel] = useState<SmArchivo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Personal
  const [pNombre, setPNombre] = useState('');
  const [pRol, setPRol] = useState('');
  const [pObs, setPObs] = useState('');
  const [addingP, setAddingP] = useState(false);

  // Actividad
  const [aTipo, setATipo] = useState('Instalacion');
  const [aDesc, setADesc] = useState('');
  const [aFecha, setAFecha] = useState('');
  const [aHoraIni, setAHoraIni] = useState('');
  const [aHoraFin, setAHoraFin] = useState('');
  const [aResp, setAResp] = useState('');
  const [addingA, setAddingA] = useState(false);

  const recargar = useCallback(async () => {
    if (!evento?.id) return;
    try {
      const res = await decoracionApi.get(evento.id);
      setDetalle(res);
    } catch {
      setDetalle(evento);
    }
  }, [evento]);

  useEffect(() => { if (isOpen) recargar(); }, [isOpen, recargar]);

  const personal: DecoracionPersonal[] = detalle?.personal || [];
  const actividades: DecoracionActividad[] = detalle?.actividades || [];
  const archivos: SmArchivo[] = detalle?.archivos || [];
  const porMomento = (m: string) => archivos.filter((a) => a.momento === m);

  // ── Personal ──
  const handleAddPersonal = async () => {
    if (!pNombre) { toast({ title: 'Nombre requerido', status: 'warning', duration: 2500 }); return; }
    setAddingP(true);
    try {
      await decoracionApi.addPersonal(detalle.id, { nombre: pNombre, rol: pRol || undefined, observaciones: pObs || undefined });
      setPNombre(''); setPRol(''); setPObs('');
      toast({ title: 'Personal agregado', status: 'success', duration: 2000 });
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setAddingP(false); }
  };

  const handleRemovePersonal = async (pid: number) => {
    try {
      await decoracionApi.removePersonal(detalle.id, pid);
      toast({ title: 'Personal eliminado', status: 'success', duration: 2000 });
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  // ── Actividades ──
  const handleAddActividad = async () => {
    if (!aTipo) { toast({ title: 'Tipo requerido', status: 'warning', duration: 2500 }); return; }
    setAddingA(true);
    try {
      await decoracionApi.addActividad(detalle.id, {
        tipo: aTipo, descripcion: aDesc || undefined, fecha: aFecha || undefined,
        horaInicio: aHoraIni || undefined, horaTermino: aHoraFin || undefined,
        responsable: aResp || undefined, estatus: 'Pendiente',
      });
      setADesc(''); setAFecha(''); setAHoraIni(''); setAHoraFin(''); setAResp('');
      toast({ title: 'Actividad agregada', status: 'success', duration: 2000 });
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setAddingA(false); }
  };

  const handleCambiarEstatusActividad = async (act: DecoracionActividad, estatus: string) => {
    try {
      await decoracionApi.updateActividad(detalle.id, act.id, { estatus });
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  const handleRemoveActividad = async (actId: number) => {
    try {
      await decoracionApi.removeActividad(detalle.id, actId);
      toast({ title: 'Actividad eliminada', status: 'success', duration: 2000 });
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  // ── Evidencias ──
  const handleSubir = async (momento: string, file?: File) => {
    if (!file) return;
    setSubiendo(momento);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('momento', momento);
      fd.append('tipoArchivo', 'foto');
      if (comentario) fd.append('comentario', comentario);
      await decoracionApi.addArchivo(detalle.id, fd);
      toast({ title: 'Evidencia subida', status: 'success', duration: 2000 });
      setComentario('');
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error al subir', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setSubiendo(null); }
  };

  const handleEliminarArchivo = (a: SmArchivo) => { setArchivoDel(a); onOpenDel(); };
  const confirmarEliminarArchivo = async () => {
    if (!archivoDel) return;
    setEliminando(true);
    try {
      await decoracionApi.removeArchivo(detalle.id, archivoDel.id);
      toast({ title: 'Evidencia eliminada', status: 'success', duration: 2000 });
      onCloseDel();
      await recargar(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setEliminando(false); }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Detalle — {detalle?.evento}
          </ModalHeader>
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              {/* ── Personal ── */}
              <SectionTitle>Personal</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2}>
                <Input size="sm" placeholder="Nombre*" value={pNombre} onChange={(e) => setPNombre(e.target.value)} />
                <Input size="sm" placeholder="Rol" value={pRol} onChange={(e) => setPRol(e.target.value)} />
                <Input size="sm" placeholder="Observaciones" value={pObs} onChange={(e) => setPObs(e.target.value)} />
                <Button size="sm" leftIcon={<FiPlus />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleAddPersonal} isLoading={addingP}>
                  Agregar
                </Button>
              </SimpleGrid>
              {personal.length === 0 ? (
                <Text fontSize="xs" color="gray.400">Sin personal registrado</Text>
              ) : (
                <VStack align="stretch" spacing={1}>
                  {personal.map((p) => (
                    <HStack key={p.id} justify="space-between" bg="gray.50" px={3} py={1.5} borderRadius="md">
                      <Text fontSize="sm"><strong>{p.nombre}</strong>{p.rol ? ` — ${p.rol}` : ''}{p.observaciones ? ` (${p.observaciones})` : ''}</Text>
                      <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleRemovePersonal(p.id)} />
                    </HStack>
                  ))}
                </VStack>
              )}

              <Divider />

              {/* ── Actividades ── */}
              <SectionTitle>Actividades</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 6 }} spacing={2}>
                <Select size="sm" value={aTipo} onChange={(e) => setATipo(e.target.value)}>
                  {TIPOS_ACT.map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
                <Input size="sm" placeholder="Descripción" value={aDesc} onChange={(e) => setADesc(e.target.value)} />
                <Input size="sm" type="date" value={aFecha} onChange={(e) => setAFecha(e.target.value)} />
                <Input size="sm" type="time" value={aHoraIni} onChange={(e) => setAHoraIni(e.target.value)} />
                <Input size="sm" placeholder="Responsable" value={aResp} onChange={(e) => setAResp(e.target.value)} />
                <Button size="sm" leftIcon={<FiPlus />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleAddActividad} isLoading={addingA}>
                  Agregar
                </Button>
              </SimpleGrid>
              {actividades.length === 0 ? (
                <Text fontSize="xs" color="gray.400">Sin actividades registradas</Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg="#5b182b">
                      <Tr>
                        <Th color="white" fontSize="xs">Tipo</Th>
                        <Th color="white" fontSize="xs">Descripción</Th>
                        <Th color="white" fontSize="xs">Fecha</Th>
                        <Th color="white" fontSize="xs">Responsable</Th>
                        <Th color="white" fontSize="xs">Estatus</Th>
                        <Th color="white" fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {actividades.map((a) => (
                        <Tr key={a.id}>
                          <Td><Badge colorScheme="purple">{a.tipo}</Badge></Td>
                          <Td fontSize="sm">{a.descripcion || '—'}</Td>
                          <Td fontSize="sm">{a.fecha ? new Date(a.fecha).toLocaleDateString('es-MX') : '—'}</Td>
                          <Td fontSize="sm">{a.responsable || '—'}</Td>
                          <Td>
                            <Select size="xs" w="130px" value={a.estatus} onChange={(e) => handleCambiarEstatusActividad(a, e.target.value)}>
                              {ESTATUS_ACT.map((s) => <option key={s} value={s}>{s}</option>)}
                            </Select>
                          </Td>
                          <Td>
                            <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleRemoveActividad(a.id)} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}

              <Divider />

              {/* ── Evidencias ── */}
              <SectionTitle>Evidencias (Antes / Después)</SectionTitle>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Comentario (opcional, se aplica a la siguiente carga)</Text>
                <Input size="sm" placeholder="Comentario para la evidencia..." value={comentario} onChange={(e) => setComentario(e.target.value)} />
              </Box>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {MOMENTOS.map((m) => (
                  <Box key={m} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                    <HStack justify="space-between" mb={2}>
                      <Badge colorScheme={m === 'ANTES' ? 'orange' : 'green'} px={2} py={0.5} borderRadius="full">{m}</Badge>
                      <Text fontSize="xs" color="gray.400">{porMomento(m).length} foto(s)</Text>
                    </HStack>
                    <input ref={m === 'ANTES' ? antesRef : despuesRef} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => { handleSubir(m, e.target.files?.[0]); e.target.value = ''; }} />
                    <Button size="sm" w="full" mb={3} leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="teal"
                      isLoading={subiendo === m} loadingText="Subiendo..."
                      onClick={() => (m === 'ANTES' ? antesRef : despuesRef).current?.click()}>
                      Agregar foto {m}
                    </Button>
                    <HStack spacing={2} flexWrap="wrap">
                      {porMomento(m).length === 0 ? (
                        <Text fontSize="xs" color="gray.400">Sin evidencias</Text>
                      ) : porMomento(m).map((a) => (
                        <Box key={a.id} position="relative" w="80px" h="80px">
                          <Box as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank" display="block" w="80px" h="80px"
                            borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
                            <img src={`${UPLOADS_BASE}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                          <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red"
                            borderRadius="full" p={0} minW="18px" h="18px" onClick={() => handleEliminarArchivo(a)}>
                            <Icon as={FiTrash2} boxSize={2.5} />
                          </Button>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
          </ModalBody>
          <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenDel} leastDestructiveRef={cancelRef} onClose={onCloseDel}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar evidencia</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{archivoDel?.nombreArchivo}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseDel} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" ml={3} onClick={confirmarEliminarArchivo} isLoading={eliminando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalDecoracionDetalle;
